import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';
import {
  validateResaleInput,
  selectSchedule,
  computeCommission,
  InstrumentClass,
  type ValidationError,
} from '@covered-calls/shared';
import type {
  BrokerProfile,
  ResaleInput,
  OptionHistoryEntry,
  CommissionResult,
  Exchange,
} from '@covered-calls/shared';

/**
 * ResaleDialog — option sale form for existing stock holdings without active option.
 * Shows cumulative option history and premium income.
 *
 * Validates: Requirements 6.4, 6.5, 6.6, 6.7, 8.1, 8.2, 8.3, 8.4, 8.5
 */

export interface ResaleDialogProps {
  positionId: string;
  ticker: string;
  quantity: number;
  /** The exchange the underlying trades on; keys the commission schedule selection. */
  exchange: Exchange;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  strikePrice: string;
  premium: string;
  expiryDate: string;
  commission: string;
  brokerProfileId: string;
}

const initialFormState: FormState = {
  strikePrice: '',
  premium: '',
  expiryDate: '',
  commission: '',
  brokerProfileId: '',
};

export function ResaleDialog({
  positionId,
  ticker,
  quantity,
  exchange,
  onSuccess,
  onCancel,
}: ResaleDialogProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>([]);
  const [optionHistory, setOptionHistory] = useState<OptionHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  // Commission/GST split from the shared calculator for the selected schedule.
  const [commissionBreakdown, setCommissionBreakdown] = useState<CommissionResult | null>(null);
  // True when a profile is selected but has no schedule for this (exchange, OPTIONS) cell.
  const [noScheduleForLeg, setNoScheduleForLeg] = useState(false);

  // Fetch broker profiles and option history on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [profiles, history] = await Promise.all([
          apiClient.get<BrokerProfile[]>('/broker-profiles'),
          apiClient.get<OptionHistoryEntry[]>(`/trades/${positionId}/option-history`),
        ]);
        setBrokerProfiles(profiles);
        setOptionHistory(history);
      } catch {
        // Non-critical: continue without profiles/history
      } finally {
        setIsLoadingHistory(false);
      }
    }
    loadData();
  }, [positionId]);

  // Calculate cumulative premium income from option history
  const cumulativePremiumIncome = optionHistory.reduce((sum, entry) => {
    const premiumReceived = entry.premium * quantity;
    const buybackCost = (entry.buybackCost ?? 0) * quantity;
    return sum + premiumReceived - buybackCost;
  }, 0);

  /**
   * Resolve the option-sale commission via the shared calculator.
   *
   * Selects the schedule for this position's (exchange, OPTIONS) cell and
   * computes the commission/GST split using tradeValue = strikePrice × quantity
   * (the resale trade value). Populates the commission field with the effective
   * `total` (commission + GST) and records the split for the preview. When the
   * profile has no schedule for this leg, falls back to manual entry.
   */
  const resolveCommission = useCallback(
    (profileId: string, strikePriceStr: string) => {
      if (!profileId) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }

      const schedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (!schedule) {
        // No schedule for this leg — fall back to manual entry (do not crash).
        setCommissionBreakdown(null);
        setNoScheduleForLeg(true);
        return;
      }

      const strikePrice = parseFloat(strikePriceStr) || 0;
      const tradeValue = strikePrice * quantity;
      const breakdown = computeCommission(schedule, { tradeValue, quantity });

      setNoScheduleForLeg(false);
      setCommissionBreakdown(breakdown);
      setForm((prev) => ({ ...prev, commission: breakdown.total.toFixed(2) }));
    },
    [brokerProfiles, exchange, quantity]
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
      // Strike price drives the resale trade value; recompute when it changes
      // and a broker profile is already selected.
      if (field === 'strikePrice' && form.brokerProfileId) {
        resolveCommission(form.brokerProfileId, value);
      }
    },
    [resolveCommission, form.brokerProfileId]
  );

  // Auto-populate commission when broker profile is selected
  const handleBrokerProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));
      resolveCommission(profileId, form.strikePrice);
      setServerError(null);
    },
    [resolveCommission, form.strikePrice]
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: ResaleInput = {
        strikePrice: parseFloat(form.strikePrice),
        premium: parseFloat(form.premium),
        expiryDate: form.expiryDate,
        commission: parseFloat(form.commission),
        brokerProfileId: form.brokerProfileId || undefined,
      };

      // Client-side validation
      const validationErrors = validateResaleInput(input);


      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await apiClient.post(`/trades/${positionId}/resell`, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 409) {
            // Active option already exists (Req 8.4)
            setServerError(
              'An active option already exists on this holding. You cannot sell a new option until the current one expires or is closed.'
            );
          } else if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((e) => ({ field: e.field, message: e.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="resale-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="resale-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Sell New Option — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Sell a new call option against your {quantity} shares of {ticker}.
        </p>

        {/* Option History Summary */}
        {!isLoadingHistory && optionHistory.length > 0 && (
          <div
            style={{
              backgroundColor: 'var(--color-surface-2)',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
            }}
          >
            <strong>Option History</strong>
            <p style={{ margin: '0.5rem 0 0' }}>
              Previous options sold: {optionHistory.length}
            </p>
            <p style={{ margin: '0.25rem 0 0' }}>
              Cumulative premium income: ${cumulativePremiumIncome.toFixed(2)}
            </p>
          </div>
        )}

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Strike Price */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="resale-strike-price"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Strike Price ($)
            </label>
            <input
              id="resale-strike-price"
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              value={form.strikePrice}
              onChange={handleFieldChange('strikePrice')}
              aria-invalid={!!getFieldError('strikePrice')}
              aria-describedby={getFieldError('strikePrice') ? 'resale-strike-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('strikePrice') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('strikePrice') && (
              <span id="resale-strike-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('strikePrice')}
              </span>
            )}
          </div>

          {/* Premium */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="resale-premium"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Premium ($ per share)
            </label>
            <input
              id="resale-premium"
              type="number"
              step="0.01"
              min="0.00"
              max="999999.99"
              value={form.premium}
              onChange={handleFieldChange('premium')}
              aria-invalid={!!getFieldError('premium')}
              aria-describedby={getFieldError('premium') ? 'resale-premium-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('premium') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('premium') && (
              <span id="resale-premium-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('premium')}
              </span>
            )}
          </div>

          {/* Expiry Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="resale-expiry-date"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Expiry Date
            </label>
            <input
              id="resale-expiry-date"
              type="date"
              value={form.expiryDate}
              onChange={handleFieldChange('expiryDate')}
              aria-invalid={!!getFieldError('expiryDate')}
              aria-describedby={getFieldError('expiryDate') ? 'resale-expiry-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('expiryDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('expiryDate') && (
              <span id="resale-expiry-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('expiryDate')}
              </span>
            )}
          </div>

          {/* Broker Profile Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="resale-broker-profile"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Broker Profile (optional)
            </label>
            <select
              id="resale-broker-profile"
              value={form.brokerProfileId}
              onChange={handleBrokerProfileChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              <option value="">-- Select broker profile --</option>
              {brokerProfiles.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.brokerName}
                </option>
              ))}
            </select>
          </div>

          {/* Commission */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="resale-commission"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Commission ($)
            </label>
            <input
              id="resale-commission"
              type="number"
              step="0.01"
              min="0.00"
              max="99999.99"
              value={form.commission}
              onChange={handleFieldChange('commission')}
              aria-invalid={!!getFieldError('commission')}
              aria-describedby={getFieldError('commission') ? 'resale-commission-error' : undefined}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('commission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('commission') && (
              <span id="resale-commission-error" style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>
                {getFieldError('commission')}
              </span>
            )}

            {/* Commission / GST split from the shared calculator (Req 10.3) */}
            {commissionBreakdown && (
              <div
                data-testid="resale-commission-breakdown"
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'var(--color-surface-2)',
                  borderRadius: '4px',
                  fontSize: '0.8125rem',
                  color: 'var(--color-muted)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Commission</span>
                  <span>${commissionBreakdown.commission.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>GST</span>
                  <span>${commissionBreakdown.gst.toFixed(2)}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 600,
                    borderTop: '1px solid var(--color-border)',
                    marginTop: '0.25rem',
                    paddingTop: '0.25rem',
                  }}
                >
                  <span>Total</span>
                  <span>${commissionBreakdown.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* No schedule for this (exchange, OPTIONS) cell — manual entry (Req 12.1) */}
            {noScheduleForLeg && (
              <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-chip-warning-text)', marginTop: '0.25rem' }}>
                This broker profile has no schedule for {exchange} options. Enter the commission manually.
              </span>
            )}

            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              Auto-populated from broker profile. Override if actual commission differs.
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Sell Option'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
